import * as React from "react";
import styles from "./ChatMessagesList.module.scss";
import { useTranslation } from "react-i18next";

import AutoScrollContainer from "@view/components/autoScrollContainer/AutoScrollContainer";
import { ChatCompletionRequestMessageRoleEnum } from "../../../app/interfaces/ChatCompletionRoleEnum";
import ChatMessage from "../chatMessage/ChatMessage";
import { ConversationMessageInterface, ModelCardInterface, ToolCallInterface } from "@dashart/dashart-gpt-shared-library";
import { ToolProgressInterface } from "../../../app/services/CustomEndpointChatService";
import ToolProgressList from "../toolProgressList/ToolProgressList";
import ModelDetails from "./modelDetails/ModelDetails";

export interface ChatMessagesListProps {
    /**
     * Custom class names
     */
    className?: string;

    /**
     * List of messages to render
     */
    messages: ConversationMessageInterface[];

    /**
     * Flag if a completion is running
     */
    completionIsRunning: boolean;

    /**
     * Current question
     */
    currentQuestion?: string;

    /**
     * Current completion
     */
    currentCompletion?: string;

    /**
     * Current reasoning
     */
    currentReasoning?: string;

    /**
     * Tool calls being streamed for the in-progress assistant turn
     */
    currentToolCalls?: ToolCallInterface[] | null;

    /**
     * Agent-style tool progress (running/completed) for the in-progress turn
     */
    currentToolProgress?: ToolProgressInterface[] | null;

    /**
     * Label if no messages
     */
    labelIfNoMessages: string;

    /**
     * Model information to display in empty state
     */
    model?: ModelCardInterface;

    /**
     * Callback when a fork request is made
     */
    onForkRequestHandler?: (messageIndex: number) => void;
}

/**
 * Render a list of chat messages in a scrollable container with auto scroll to bottom
 * 
 * @param props 
 * @returns 
 */
const ChatMessagesList: React.FunctionComponent<ChatMessagesListProps> = (props: ChatMessagesListProps) => {
    const { t } = useTranslation();
    const {
        className,
        onForkRequestHandler,
        messages,
        completionIsRunning,
        currentQuestion,
        currentCompletion,
        currentReasoning,
        currentToolCalls,
        currentToolProgress,
        labelIfNoMessages,
        model
    } = props;
    const scrollToBottomTargetRef = React.useRef<HTMLDivElement>(null);

    const messagesCount = React.useMemo(() => {
        return messages.length;
    }, [messages]);

    /**
     * Render messages already completed,
     * this will increase performance for long conversations
     */
    const renderedCompletedMessage = React.useMemo(() => {
        return (messages ?? []).map((message, index) => <ChatMessage
            key={index}
            message={message}
            index={index}
            onForkRequestHandler={onForkRequestHandler}
        />)
    }, [messages])

    /**
     * Cached current question message rendering for performance reasons
     */
    const currentCompletionMessageRendered = React.useMemo(() => {
        if (!completionIsRunning) {
            return null;
        }
        return <ChatMessage
            message={{
                message: {
                    role: ChatCompletionRequestMessageRoleEnum.User,
                    content: currentQuestion
                }
            }}
            index={messages.length}
            onForkRequestHandler={onForkRequestHandler}
        />
    }, [completionIsRunning, currentQuestion, messages]);

    /**
     * Live agent tool progress (running/completed/failed) rendered while the
     * assistant turn is streaming.
     */
    const currentToolProgressRendered = React.useMemo(() => {
        if (!completionIsRunning || !currentToolProgress?.length) {
            return null;
        }
        return <ToolProgressList progress={currentToolProgress} />;
    }, [completionIsRunning, currentToolProgress]);

    /**
     * Render messages list from top to bottom
     */
    const renderMessages = React.useMemo(() => {
        const messagesCount = messages.length;

        return <div className={'flex flex-column'}>
            {/* Render messages from top to bottom */}
            {renderedCompletedMessage}

            {/* Render current question if completion is running */}
            {currentCompletionMessageRendered}

            {/* Render live agent tool progress while completion is running */}
            {currentToolProgressRendered}

            {/* Render current completion if completion is running */}
            {completionIsRunning ? <ChatMessage
                message={{
                    message: {
                        role: ChatCompletionRequestMessageRoleEnum.Assistant,
                        content: currentCompletion,
                        ...(currentReasoning !== undefined && currentReasoning !== null ? { reasoning: currentReasoning } : {}),
                        ...(currentToolCalls?.length !== undefined && currentToolCalls?.length > 0 ? { tool_calls: currentToolCalls } : {})
                    }
                }}
                index={messagesCount + 1}
                onForkRequestHandler={onForkRequestHandler}
            /> : null}
        </div>
    }, [messages, currentCompletion, currentReasoning, currentToolCalls, completionIsRunning, onForkRequestHandler, renderedCompletedMessage, currentCompletionMessageRendered, currentToolProgressRendered]);

    return <AutoScrollContainer
        className={`${styles.ChatMessagesList} ${className}`}
        magicPullZoneHeight={30}
    >
        {renderMessages}

        {(messagesCount === 0 && !completionIsRunning) ? <div className={'w-100 mt-4'}>
            <div className={'col-12 text-center mb-4'}>
                {labelIfNoMessages}
            </div>
            {model && (
                <div className={'col-12 d-flex flex-column justify-content-center align-items-center'}>
                    <ModelDetails model={model} />
                </div>
            )}
        </div> : null}

        {/*auto scroll to bottom target*/}
        <div ref={scrollToBottomTargetRef} />
    </AutoScrollContainer>
}

export default ChatMessagesList;